import React from 'react';
import { BoxProps, Typography, Box } from '@material-ui/core';
import { Overrideable } from '@lib/state';

interface Props extends BoxProps {
  override: Overrideable;
  remainingStayDays: number;
  refundableStayDays: number;
  hasPendingCharges: boolean;
  canRefundWithOverride: boolean;
}

/**
 * Builds the user-facing description of how many stay days can be dropped under
 * policy and whether a manager override is required for the rest.
 */
export const getRefundableDaysMessage = (
  override: Overrideable,
  remainingStayDays: number,
  refundableStayDays: number
): string => {
  // the api provides the number of refundable stay days without a manager override
  const policyMessage = `Based on the policy for this reservation, ${refundableStayDays} of the remaining ${remainingStayDays} days can be refunded.`;

  // override is set by the parent component based on whether the reservation can be refunded further with a manager override
  if (override === 'unavailable') return policyMessage;

  // the system does not allow you to refund past stay dates
  return `${policyMessage} With a manager override, all ${remainingStayDays} remaining days can be refunded.`;
};

export const RefundableDaysTextDisplay: React.FC<Props> = ({
  override = 'unavailable',
  remainingStayDays,
  refundableStayDays,
  hasPendingCharges,
  canRefundWithOverride,
  ...props
}) => {
  const message = getRefundableDaysMessage(override, remainingStayDays, refundableStayDays);

  return (
    <>
      <Box {...props}>
        <Typography>{message}</Typography>
      </Box>
      <Box {...props}>
        {hasPendingCharges && (
          <Typography>
            This reservation has pending charges that
            {override !== 'unavailable' && canRefundWithOverride
              ? ' will need a manager override to be refunded.'
              : ' cannot be refunded at this time. Please contact the front desk for assistance.'}
          </Typography>
        )}
      </Box>
    </>
  );
};
